import React, { useState, useEffect, useMemo } from "react";
import { TEAM_INFO } from "../../lib/teamMap";
import { ALL_NCAA_SCHOOLS } from "../../lib/ncaaData";
import { api } from "../../lib/api";
import { parseFlexibleDate } from "../../lib/date";
import { CONTRACT_SEASON_OPTIONS, formatMoney as fmtMoney, cellForPlayerSeason } from "../../lib/contracts";
import { AlertCircle, Plus, X } from "lucide-react";

export const TEAM_IDS = Object.keys(TEAM_INFO);
export const ACTIVE_PWHL_TEAM_IDS = ["bos", "min", "mtl", "nyc", "ott", "sea", "tor", "van"];
export const PWHL_TEAM_IDS = TEAM_IDS;
// ---------- helpers ----------
export function calculateAge(birthdate) {
    const dob = parseFlexibleDate(birthdate);
    if (!dob) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age -= 1;
    return age >= 0 && age < 120 ? age : null;
}

export const COACH_ROLES = ["General Manager", "Head Coach", "Head Coach & General Manager", "Assistant Coach", "Video Coach", "Other"];
export const COACH_LEAGUES = ["PWHL", "NCAA", "SDHL", "Other"];

// Convert centimetres → imperial feet/inches label, e.g. 175 → 5'9".
export const cmToFtIn = (cm) => {
    const n = Number(cm) || 0;
    if (n <= 0) return "";
    const totalIn = Math.round(n / 2.54);
    return `${Math.floor(totalIn / 12)}'${totalIn % 12}"`;
};
export const STAFF_SEASON_OPTIONS = (() => {
    const out = ["2024"];
    for (let y = 2024; y <= 2030; y++) {
        out.unshift(`${y}-${String(y + 1).slice(2)}`);
    }
    return out;
})();
export const CONTRACT_STATUS = ["", "UFA", "RFA", "Reserve", "SPA", "10 Days"];
export const LEAGUES = ["PWHL", "NCAA", "SDHL"];
export const POSITIONS = ["F", "LW", "C", "RW", "W", "C/W", "D", "LD", "RD", "G"];
export const CURRENT_SEASON = "2025-26";

// Seasons shown on the contract grid imported from ../lib/contracts

// US-style money formatter (also exported from /lib/contracts as fmtMoney for shared use)
export const formatMoney = fmtMoney;
// Parse "$84,872.50" / "84,872.50" / "84872.5" -> Number
export const parseMoney = (str) => {
    if (str === null || str === undefined) return 0;
    const clean = String(str).replace(/[^\d.-]/g, "");
    return parseFloat(clean) || 0;
};

export const MoneyInput = ({ value, onChange, testId, placeholder = "0.00", className = "" }) => {
    const [text, setText] = useState(value ? formatMoney(value) : "");
    // Sync external changes (e.g. on edit) using the render-time adjust pattern
    // (no setState-in-effect, which CRA's eslint rejects).
    const [prevValue, setPrevValue] = useState(value);
    if (value !== prevValue) {
        setPrevValue(value);
        setText(value ? formatMoney(value) : "");
    }
    const handleChange = (e) => {
        // Allow user to type freely (digits, dot, commas); reformat on blur
        setText(e.target.value);
    };
    const handleBlur = () => {
        const num = parseMoney(text);
        onChange?.(num);
        setText(num ? formatMoney(num) : "");
    };
    return (
        <div className={`relative ${className}`}>
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[#94A3B8] text-sm pointer-events-none">$</span>
            <input
                data-testid={testId}
                type="text"
                inputMode="decimal"
                value={text}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={placeholder}
                className={inputCls + " pl-6 font-mono-stat"}
            />
        </div>
    );
};

// For destructive actions: user typed "DELETE" to confirm.
export function confirmDelete(itemDescription) {
    if (!window.confirm(`Are you sure you want to delete ${itemDescription}?`)) return false;
    const pw = window.prompt(`Type DELETE (in capital letters) to confirm deletion of ${itemDescription}:`);
    if (pw !== "DELETE") {
        if (pw !== null) window.alert("Confirmation did not match. Deletion cancelled.");
        return false;
    }
    return true;
}

// Master date system: returns "expired" if season is at/before current, "expiring" if equals current
export const seasonStatus = (s) => {
    if (!s) return null;
    if (s === CURRENT_SEASON) return "expiring";
    // crude lexical compare works for "YYYY-YY"
    return s < CURRENT_SEASON ? "expired" : null;
};

// Re-verify admin password before any destructive action.
async function confirmPassword(email) {
    const pw = window.prompt("Confirm your admin password to continue:");
    if (!pw) return false;
    try {
        await api.post("/auth/login", { email, password: pw });
        return true;
    } catch {
        window.alert("Wrong password. Action cancelled.");
        return false;
    }
}

// 2030-31 back to 2000-01
export const SEASON_OPTIONS = (() => {
    const arr = [];
    for (let start = 2030; start >= 2000; start--) {
        arr.push(`${start}-${String(start + 1).slice(-2)}`);
    }
    return arr;
})();

export const ContractBadge = ({ endSeason, status }) => {
    const ss = seasonStatus(endSeason);
    let cls = "bg-[#1E2233] text-[#94A3B8]";
    let label = status || "No contract info";
    if (status === "UFA") cls = "bg-[#EF4444]/20 text-[#FCA5A5] border border-[#EF4444]/40";
    else if (status === "RFA") cls = "bg-[#3B82F6]/20 text-[#93C5FD] border border-[#3B82F6]/40";
    else if (status === "Signed") cls = "bg-[#10B981]/15 text-[#6EE7B7] border border-[#10B981]/40";
    else if (status === "Reserve") cls = "bg-[#F97316]/20 text-[#FDBA74] border border-[#F97316]/40";
    else if (status === "SPA") cls = "bg-[#EC4899]/20 text-[#F9A8D4] border border-[#EC4899]/40";
    else if (status === "10 Days") cls = "bg-[#14B8A6]/20 text-[#5EEAD4] border border-[#14B8A6]/40";
    return (
        <div className="space-y-1" data-testid="contract-badge">
            <div className={`inline-flex items-center px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${cls}`}>
                {label}{endSeason ? ` · ends ${endSeason}` : ""}
            </div>
            {ss === "expiring" && (
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#F59E0B] inline-flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> Expires this season — needs update
                </p>
            )}
            {ss === "expired" && (
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#EF4444] inline-flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> Contract expired — needs update
                </p>
            )}
        </div>
    );
};

export const blankSkaterRow = () => ({
    season: "", team: "", league: "PWHL",
    gp: 0, g: 0, a: 0, p: 0, pim: 0, plus_minus: 0,
    po_gp: 0, po_g: 0, po_a: 0, po_p: 0, po_pim: 0, po_plus_minus: 0,
});
export const blankGoalieRow = () => ({
    season: "", team: "", league: "PWHL",
    gp: 0, w: 0, l: 0, otl: 0, gaa: 0, sv_pct: 0, so: 0,
    po_gp: 0, po_w: 0, po_l: 0, po_otl: 0, po_gaa: 0, po_sv_pct: 0, po_so: 0,
});
export const blankSalary = () => ({ year: "", amount: 0, team: "" });
export const blankCareer = () => ({ season: "", team: "", league: "PWHL" });

export const blankPlayer = () => ({
    name: "", jersey: 0, position: "F", pref_position: "",
    photo_x: 50, photo_y: 50, photo_zoom: 1,
    league: "PWHL", team_id: "ott", team_name: "",
    photo_url: "",
    nationality: "",
    birthplace: "", birthdate: "", shoots: "", height_cm: 0,
    eliteprospects_url: "",
    current_salary: 0, contract_start_season: "", contract_end_season: "", contract_status: "",
    contracts: [],
    active: true,
    retirement_date: "",
    retirement_note: "",
    retirement_type: "",
    retirement_season: "",
    hof_year: "",
    hof_note: "",
    hall_of_fame: false,
    declared_draft_year: "",
    draft_ranking: "",
    draft_tier: "",
    entry_method: "",
    entry_specify: "",
    entry_draft_pick_id: "",
    class_year: "",
    eligibility_remaining: 0,
    draft_eligibility_year: "",
    awards: [],
    trophies_medals: [],
    career_history: [],
    salary: [],
    pwhl: [],
    ncaa: [],
    international: [],
    other: [],
});

export const blankCoach = () => ({
    name: "", team_id: "ott", role: "Head Coach", custom_role: "",
    tenure_from: "", tenure_to: "", notes: "",
    active: true, retirement_date: "", retirement_note: "",
    image_url: "", photo_x: 50, photo_y: 50, photo_zoom: 1,
    staff_history: [],
});

// Normalise a stored coach role to the current option set (back-compat for "GM").
export const normalizeCoachRole = (role) => {
    if (role === "GM") return "General Manager";
    return COACH_ROLES.includes(role) ? role : (role ? "Other" : "Head Coach");
};

// Reasonable year range for coach tenures
export const COACH_YEARS = (() => {
    const arr = [];
    for (let y = 2030; y >= 2000; y--) arr.push(String(y));
    return arr;
})();

// ===== SHARED ELEMENTS (form helpers + editors) =====
export const inputCls = "w-full bg-[#0A0B10] border border-[#1E2233] focus:border-[#9D4CDD] outline-none text-white placeholder-[#64748B] px-3 py-2 rounded-md text-sm transition-colors";

export const Field = ({ label, wide, children }) => (
    <label className={`block ${wide ? "md:col-span-2 lg:col-span-2" : ""}`}>
        <span className="block text-[10px] uppercase tracking-wider font-bold text-[#64748B] mb-1">{label}</span>
        {children}
    </label>
);

// Season dropdown options per league section.
// PWHL first season took place ENTIRELY in 2024 (the inaugural year was a single-year
// "2024" season). After that the league moved to season-spanning naming (2024-25 etc).
export const buildSeasonRange = (startYear, endYear) => {
    const out = [];
    for (let y = endYear; y >= startYear; y--) out.push(`${y}-${String(y + 1).slice(2)}`);
    return out;
};
export const STAT_SEASON_OPTIONS = {
    pwhl: ["2030-31", "2029-30", "2028-29", "2027-28", "2026-27", "2025-26", "2024-25", "2024"],
    ncaa: buildSeasonRange(2010, 2030),
    international: buildSeasonRange(1990, 2030),
};

// Build "School (Conference)" labels for NCAA team selector
export const NCAA_SCHOOL_OPTIONS = ALL_NCAA_SCHOOLS.map(({ school, conference }) => ({
    value: school,
    label: `${school} (${conference})`,
}));

export const StatEditor = ({ title, league, isGoalie, rows, onAdd, onRemove, onChange }) => {
    const seasonOpts = STAT_SEASON_OPTIONS[league] || STAT_SEASON_OPTIONS.pwhl;
    const regCols = isGoalie
        ? [
            { key: "gp", label: "GP" }, { key: "w", label: "W" }, { key: "l", label: "L" },
            { key: "otl", label: "OTL" }, { key: "gaa", label: "GAA", step: "0.01" },
            { key: "sv_pct", label: "SV%", step: "0.001" }, { key: "so", label: "SO" },
        ]
        : [
            { key: "gp", label: "GP" }, { key: "g", label: "G" }, { key: "a", label: "A" },
            { key: "p", label: "P" }, { key: "pim", label: "PIM" }, { key: "plus_minus", label: "+/-" },
        ];
    const poCols = isGoalie
        ? [
            { key: "po_gp", label: "PO GP" }, { key: "po_w", label: "PO W" }, { key: "po_l", label: "PO L" },
            { key: "po_otl", label: "PO OTL" }, { key: "po_gaa", label: "PO GAA", step: "0.01" },
            { key: "po_sv_pct", label: "PO SV%", step: "0.001" }, { key: "po_so", label: "PO SO" },
        ]
        : [
            { key: "po_gp", label: "PO GP" }, { key: "po_g", label: "PO G" }, { key: "po_a", label: "PO A" },
            { key: "po_p", label: "PO P" }, { key: "po_pim", label: "PO PIM" }, { key: "po_plus_minus", label: "PO +/-" },
        ];
    return (
        <div data-testid={`stat-editor-${league}`}>
            <div className="flex items-center justify-between mb-2">
                <h4 className="font-heading text-lg font-bold uppercase tracking-tight">{title}</h4>
                <button type="button" onClick={onAdd} className="btn-secondary text-xs inline-flex items-center gap-1" data-testid={`add-${league}-row`}>
                    <Plus className="h-3 w-3" /> Add season
                </button>
            </div>
            {rows.length === 0 ? (
                <p className="text-xs text-[#64748B] italic">No {title.toLowerCase()} entered.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-left text-[#64748B]">
                                <th colSpan={3} className="pb-1 pr-2 text-[10px] uppercase tracking-wider font-bold">Identity</th>
                                <th colSpan={regCols.length} className="pb-1 pr-2 text-[10px] uppercase tracking-wider font-bold border-l border-[#1E2233] pl-2">Regular Season</th>
                                <th colSpan={poCols.length} className="pb-1 pr-2 text-[10px] uppercase tracking-wider font-bold text-[#9D4CDD] border-l border-[#1E2233] pl-2">Playoffs</th>
                                <th></th>
                            </tr>
                            <tr className="text-left text-[#94A3B8]">
                                <th className="pb-1 pr-2">Season</th>
                                <th className="pb-1 pr-2">Team</th>
                                <th className="pb-1 pr-2">Lge</th>
                                {regCols.map((c, i) => (
                                    <th key={c.key} className={`pb-1 pr-2 ${i === 0 ? "border-l border-[#1E2233] pl-2" : ""}`}>{c.label}</th>
                                ))}
                                {poCols.map((c, i) => (
                                    <th key={c.key} className={`pb-1 pr-2 text-[#9D4CDD] ${i === 0 ? "border-l border-[#1E2233] pl-2" : ""}`}>{c.label}</th>
                                ))}
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={i}>
                                    <td className="pr-1 pb-1">
                                        <select
                                            value={r.season ?? ""}
                                            onChange={(e) => onChange(i, "season", e.target.value)}
                                            className={inputCls + " text-xs py-1.5 min-w-[100px]"}
                                            data-testid={`${league}-${i}-season`}
                                        >
                                            <option value="">—</option>
                                            {seasonOpts.map((s) => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </td>
                                    <td className="pr-1 pb-1">
                                        {league === "pwhl" ? (
                                            <select
                                                value={r.team ?? ""}
                                                onChange={(e) => onChange(i, "team", e.target.value)}
                                                className={inputCls + " text-xs py-1.5 min-w-[140px]"}
                                                data-testid={`${league}-${i}-team`}
                                            >
                                                <option value="">—</option>
                                                {PWHL_TEAM_IDS
                                                    .filter((id) => teamEligibleForSeason(id, r.season || "2030-31"))
                                                    .map((id) => (
                                                        <option key={id} value={id}>{TEAM_INFO[id].name}</option>
                                                    ))}
                                            </select>
                                        ) : league === "ncaa" ? (
                                            <select
                                                value={r.team ?? ""}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    onChange(i, "team", v);
                                                    // Auto-fill league with conference abbreviation
                                                    const found = ALL_NCAA_SCHOOLS.find((s) => s.school === v);
                                                    if (found) onChange(i, "league", found.conference);
                                                }}
                                                className={inputCls + " text-xs py-1.5 min-w-[200px]"}
                                                data-testid={`${league}-${i}-team`}
                                            >
                                                <option value="">—</option>
                                                {NCAA_SCHOOL_OPTIONS.map((o) => (
                                                    <option key={o.value} value={o.value}>{o.label}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input value={r.team ?? ""} onChange={(e) => onChange(i, "team", e.target.value)} className={inputCls + " text-xs py-1.5"} data-testid={`${league}-${i}-team`} placeholder="e.g. Canada" />
                                        )}
                                    </td>
                                    <td className="pr-1 pb-1">
                                        {league === "pwhl" ? (
                                            <span className="text-xs px-2 py-1.5 text-[#94A3B8] block" data-testid={`${league}-${i}-league`}>PWHL</span>
                                        ) : (
                                            <input value={r.league ?? ""} onChange={(e) => onChange(i, "league", e.target.value)} className={inputCls + " text-xs py-1.5"} data-testid={`${league}-${i}-league`} />
                                        )}
                                    </td>
                                    {regCols.map((c, idx) => (
                                        <td key={c.key} className={`pr-1 pb-1 ${idx === 0 ? "border-l border-[#1E2233] pl-2" : ""}`}>
                                            <input
                                                value={r[c.key] ?? 0}
                                                onChange={(e) => onChange(i, c.key, e.target.value)}
                                                type="number" step={c.step || "1"}
                                                className={inputCls + " text-xs py-1.5 w-16"}
                                                data-testid={`${league}-${i}-${c.key}`}
                                            />
                                        </td>
                                    ))}
                                    {poCols.map((c, idx) => (
                                        <td key={c.key} className={`pr-1 pb-1 ${idx === 0 ? "border-l border-[#1E2233] pl-2" : ""}`}>
                                            <input
                                                value={r[c.key] ?? 0}
                                                onChange={(e) => onChange(i, c.key, e.target.value)}
                                                type="number" step={c.step || "1"}
                                                className={inputCls + " text-xs py-1.5 w-16"}
                                                data-testid={`${league}-${i}-${c.key}`}
                                            />
                                        </td>
                                    ))}
                                    <td><button type="button" onClick={() => onRemove(i)} className="text-[#94A3B8] hover:text-[#EF4444]"><X className="h-4 w-4" /></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export const SalaryEditor = ({ rows, onAdd, onRemove, onChange }) => (
    <div data-testid="salary-editor">
        <div className="flex items-center justify-between mb-2">
            <h4 className="font-heading text-lg font-bold uppercase tracking-tight">Salary</h4>
            <button type="button" onClick={onAdd} className="btn-secondary text-xs inline-flex items-center gap-1" data-testid="add-salary-row">
                <Plus className="h-3 w-3" /> Add year
            </button>
        </div>
        {rows.length === 0 ? (
            <p className="text-xs text-[#64748B] italic">No salary entries.</p>
        ) : (
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead><tr className="text-left text-[#94A3B8]">
                        <th className="pb-1 pr-2 w-32">Year</th>
                        <th className="pb-1 pr-2">Amount (USD)</th>
                        <th className="pb-1 pr-2">Team</th>
                        <th></th>
                    </tr></thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i}>
                                <td className="pr-1 pb-1">
                                    <input value={r.year} onChange={(e) => onChange(i, "year", e.target.value)} placeholder="2025-26" className={inputCls + " text-xs py-1.5"} data-testid={`salary-${i}-year`} />
                                </td>
                                <td className="pr-1 pb-1">
                                    <input type="number" value={r.amount} onChange={(e) => onChange(i, "amount", e.target.value)} placeholder="0" className={inputCls + " text-xs py-1.5"} data-testid={`salary-${i}-amount`} />
                                </td>
                                <td className="pr-1 pb-1">
                                    <input value={r.team} onChange={(e) => onChange(i, "team", e.target.value)} placeholder="Team" className={inputCls + " text-xs py-1.5"} data-testid={`salary-${i}-team`} />
                                </td>
                                <td><button type="button" onClick={() => onRemove(i)} className="text-[#94A3B8] hover:text-[#EF4444]"><X className="h-4 w-4" /></button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </div>
);

export const CareerEditor = ({ rows, onAdd, onRemove, onChange }) => (
    <div data-testid="career-editor">
        <div className="flex items-center justify-between mb-2">
            <h4 className="font-heading text-lg font-bold uppercase tracking-tight">Career History</h4>
            <button type="button" onClick={onAdd} className="btn-secondary text-xs inline-flex items-center gap-1">
                <Plus className="h-3 w-3" /> Add entry
            </button>
        </div>
        {rows.length === 0 ? (
            <p className="text-xs text-[#64748B] italic">No career history entered.</p>
        ) : (
            <div className="space-y-2">
                {rows.map((r, i) => (
                    <div key={i} className="flex gap-2">
                        <input value={r.season} onChange={(e) => onChange(i, "season", e.target.value)} placeholder="2023-" className={inputCls + " text-xs py-1.5 w-32"} />
                        <input value={r.team} onChange={(e) => onChange(i, "team", e.target.value)} placeholder="Team / School" className={inputCls + " text-xs py-1.5 flex-1"} />
                        <select value={r.league} onChange={(e) => onChange(i, "league", e.target.value)} className={inputCls + " text-xs py-1.5 w-28"}>
                            <option value="PWHL">PWHL</option>
                            <option value="NCAA">NCAA</option>
                            <option value="SDHL">SDHL</option>
                            <option value="IIHF">IIHF</option>
                            <option value="Other">Other</option>
                        </select>
                        <button type="button" onClick={() => onRemove(i)} className="text-[#94A3B8] hover:text-[#EF4444]"><X className="h-4 w-4" /></button>
                    </div>
                ))}
            </div>
        )}
    </div>
);

export const StaffHistoryEditor = ({ rows, onChange }) => {
    const blank = () => ({ team_id: "", league: "PWHL", role: "Head Coach", custom_role: "", start_season: "", end_season: "", notes: "" });
    const add = () => onChange([{ ...blank() }, ...(rows || [])]);
    const remove = (i) => onChange(rows.filter((_, j) => j !== i));
    const set = (i, k, v) => onChange(rows.map((r, j) => j === i ? { ...r, [k]: v } : r));

    return (
        <div className="rosterly-card p-4 border border-[#1E2233]" data-testid="staff-history-editor">
            <div className="flex items-center justify-between mb-2">
                <div>
                    <h4 className="font-heading text-lg font-bold uppercase tracking-tight">Staff History</h4>
                    <p className="text-[11px] text-[#94A3B8]">Track this person&apos;s roles across teams and seasons (similar to player career history).</p>
                </div>
                <button type="button" onClick={add} className="btn-secondary text-xs inline-flex items-center gap-1" data-testid="staff-history-add">
                    <Plus className="h-3 w-3" /> Add entry
                </button>
            </div>
            {rows.length === 0 ? (
                <p className="text-xs text-[#64748B] italic">No staff history entries.</p>
            ) : (
                <div className="space-y-2">
                    {rows.map((r, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-end bg-[#0A0B10] border border-[#1E2233] rounded-md p-2" data-testid={`staff-history-row-${i}`}>
                            <div className="col-span-3">
                                <label className="text-[10px] uppercase tracking-wider text-[#64748B] font-bold">Team</label>
                                <select
                                    value={r.team_id || ""}
                                    onChange={(e) => set(i, "team_id", e.target.value)}
                                    className={inputCls + " text-xs py-1.5"}
                                    data-testid={`staff-history-${i}-team`}
                                >
                                    <option value="">— (no team)</option>
                                    {Object.keys(TEAM_INFO).map((id) => (
                                        <option key={id} value={id}>{TEAM_INFO[id].name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-span-2">
                                <label className="text-[10px] uppercase tracking-wider text-[#64748B] font-bold">League</label>
                                <select
                                    value={r.league || "PWHL"}
                                    onChange={(e) => set(i, "league", e.target.value)}
                                    className={inputCls + " text-xs py-1.5"}
                                    data-testid={`staff-history-${i}-league`}
                                >
                                    {COACH_LEAGUES.map((lg) => <option key={lg} value={lg}>{lg}</option>)}
                                </select>
                            </div>
                            <div className="col-span-2">
                                <label className="text-[10px] uppercase tracking-wider text-[#64748B] font-bold">Role</label>
                                <select
                                    value={r.role || "Head Coach"}
                                    onChange={(e) => set(i, "role", e.target.value)}
                                    className={inputCls + " text-xs py-1.5"}
                                    data-testid={`staff-history-${i}-role`}
                                >
                                    {COACH_ROLES.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                                </select>
                            </div>
                            {r.role === "Other" && (
                                <div className="col-span-2">
                                    <label className="text-[10px] uppercase tracking-wider text-[#64748B] font-bold">Custom</label>
                                    <input
                                        value={r.custom_role || ""}
                                        onChange={(e) => set(i, "custom_role", e.target.value)}
                                        placeholder="e.g. Goaltending"
                                        className={inputCls + " text-xs py-1.5"}
                                        data-testid={`staff-history-${i}-custom-role`}
                                    />
                                </div>
                            )}
                            <div className={r.role === "Other" ? "col-span-1" : "col-span-2"}>
                                <label className="text-[10px] uppercase tracking-wider text-[#64748B] font-bold">Start</label>
                                <select
                                    value={r.start_season || ""}
                                    onChange={(e) => set(i, "start_season", e.target.value)}
                                    className={inputCls + " text-xs py-1.5"}
                                    data-testid={`staff-history-${i}-start`}
                                >
                                    <option value="">—</option>
                                    {STAFF_SEASON_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div className={r.role === "Other" ? "col-span-1" : "col-span-2"}>
                                <label className="text-[10px] uppercase tracking-wider text-[#64748B] font-bold">End</label>
                                <select
                                    value={r.end_season || ""}
                                    onChange={(e) => set(i, "end_season", e.target.value)}
                                    className={inputCls + " text-xs py-1.5"}
                                    data-testid={`staff-history-${i}-end`}
                                >
                                    <option value="">Present</option>
                                    {STAFF_SEASON_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div className="col-span-1 text-right">
                                <button type="button" onClick={() => remove(i)} className="text-[#94A3B8] hover:text-[#EF4444]" data-testid={`staff-history-${i}-remove`}>
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};


export const AwardsEditor = ({ awards, onChange, title = "Awards", placeholder = "e.g. Patty Kazmaier Finalist", icon = "▪", testId = "awards-editor" }) => {
    const [value, setValue] = useState("");
    return (
        <div data-testid={testId}>
            <div className="flex items-center justify-between mb-2">
                <h4 className="font-heading text-lg font-bold uppercase tracking-tight">{title}</h4>
            </div>
            <div className="flex gap-2 mb-2">
                <input
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={placeholder}
                    className={inputCls + " text-xs py-1.5"}
                    data-testid={`${testId}-input`}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && value.trim()) {
                            e.preventDefault();
                            onChange([...awards, value.trim()]);
                            setValue("");
                        }
                    }}
                />
                <button type="button" onClick={() => { if (value.trim()) { onChange([...awards, value.trim()]); setValue(""); } }} className="btn-secondary text-xs" data-testid={`${testId}-add`}>Add</button>
            </div>
            {awards.length === 0 ? (
                <p className="text-xs text-[#64748B] italic">None entered.</p>
            ) : (
                <ul className="space-y-1">
                    {awards.map((a, i) => (
                        <li key={i} className="text-sm flex items-center gap-2">
                            <span className="text-[#9D4CDD]">{icon}</span>
                            <span className="flex-1">{a}</span>
                            <button type="button" onClick={() => onChange(awards.filter((_, j) => j !== i))} className="text-[#94A3B8] hover:text-[#EF4444]"><X className="h-3 w-3" /></button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

// ===== PWHL helpers =====
// 2-wave PWHL expansion (mirrors backend TEAM_FIRST_SEASON).
export const TEAM_FIRST_SEASON = {
    sea: "2025-26",
    van: "2025-26",
    det: "2026-27",
    ham: "2026-27",
    sj: "2026-27",
    lvs: "2026-27",
};
export const teamEligibleForSeason = (id, season) => {
    const first = TEAM_FIRST_SEASON[id];
    if (!first) return true;
    return season >= first;
};
export const teamsForSeason = (season) => TEAM_IDS.filter((id) => teamEligibleForSeason(id, season));
export const activeTeamsForSeason = (season) => ACTIVE_PWHL_TEAM_IDS.filter((id) => teamEligibleForSeason(id, season));

// PWHL seasons listed in order of recency
export const PWHL_SEASONS = ["2026-27", "2025-26", "2024-25", "2024"];

export const PWHL_STAND_BLANK = (id, season) => ({ team_id: id, season, gp: 0, w: 0, otw: 0, otl: 0, l: 0, pts: 0, gf: 0, ga: 0, next_opponent: "" });

export const todayStr = () => new Date().toISOString().slice(0, 10);
